import React, { useMemo } from 'react';

import { ApolloClient, type ApolloLink, InMemoryCache } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { HttpLink } from '@apollo/client/link/http';
import { ApolloProvider } from '@apollo/client/react';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

import { protectedResources } from '../../config/authConfig';
import { requestAccessToken } from '../../services/Msal/MsalService';

type ApolloContextProviderProps = {
  children: React.ReactNode;
};

const getApolloClient = (instance: IPublicClientApplication) => {
  const mlGraphEndpoint = import.meta.env.VITE_ML_GRAPH_ENDPOINT;
  const uri = mlGraphEndpoint;
  const authLink: ApolloLink = new SetContextLink(async (prevContext) => {
    const token = await requestAccessToken(instance, protectedResources.graphql.scopes);
    return {
      headers: {
        ...prevContext.headers,
        Authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const httpLink: ApolloLink = new HttpLink({
    uri,
  });

  const apolloClient = new ApolloClient({
    cache: new InMemoryCache(),
    link: httpLink ? authLink.concat(httpLink) : authLink,
  });
  return apolloClient;
};

const ApolloContextProvider = ({ children }: ApolloContextProviderProps) => {
  const { instance } = useMsal();

  // Memoize the Apollo Client to prevent recreating it on every render
  const client = useMemo(() => getApolloClient(instance), [instance]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloContextProvider;
